import type { DateTime, EventAttributes } from 'ics';
import { createEvent } from 'ics';

export enum MakeCalendarDeliveryMethod {
  return = 0,
  redirect = 1,
}

export enum MakeCalendarType {
  ics = 'ics',
  gmail = 'gmail',
}

type MinutesAndHoursResponse = {
  minutes: number;
  hours: number;
};

// Legacy Approach
export class Utilities {
  static trimOrNull = (s: string | null | undefined) => {
    const t = (s ?? '').trim();
    return t ? t : null;
  };

  static getNameInitials(
    firstRaw: string | null | undefined,
    lastRaw: string | null | undefined
  ): string {
    // First alphabetic letter
    const firstLetter = (s: string): string => {
      for (let i = 0; i < s.length; i++) {
        const ch = s.charAt(i);
        if (/[A-Za-z]/.test(ch)) return ch;
      }
      return '';
    };

    // First two alphabetic letters
    const firstTwoLetters = (s: string): string => {
      let out = '';
      for (let i = 0; i < s.length && out.length < 2; i++) {
        const ch = s.charAt(i);
        if (/[A-Za-z]/.test(ch)) out += ch;
      }
      return out;
    };

    const first = this.trimOrNull(firstRaw);
    const last = this.trimOrNull(lastRaw);

    if (first && last) {
      return (firstLetter(first) + firstLetter(last)).toUpperCase();
    }

    const source = first ?? last!;
    const two = firstTwoLetters(source);

    if (!two) return '?';

    const a = two[0]?.toUpperCase() ?? '';
    const b = two[1]?.toLowerCase() ?? '';

    return a + b;
  }

  static dateMonthToTwoDigitString(month: number): string {
    return this.dateComponentToTwoDigitString(month + 1);
  }

  static dateComponentToTwoDigitString(component: number): string {
    return ('0' + component).slice(-2);
  }

  static dateStringToDate(dateTime): Date {
    const t = (dateTime.date || dateTime).split(/[-]/);
    const d = new Date(Date.UTC(t[0], t[1] - 1, t[2]));
    d.setHours(0, 0, 0, 0);
    return d;
  }

  static dateTimeStringToDate(dateTime): Date {
    if (!dateTime) {
      return null;
    }

    const t = dateTime.date.split(/[- :]/);
    const d = new Date(Date.UTC(t[0], t[1] - 1, t[2], t[3], t[4], t[5]));
    d.setHours(0, 0, 0, 0);
    return d;
  }

  static dateTimeStringToDateTime(dateTime, isUTC = true): Date {
    if (!dateTime) {
      return null;
    }

    const t = (dateTime.date || dateTime).split(/[- :TZ]/);

    let d;

    if (isUTC) {
      d = new Date(Date.UTC(t[0], t[1] - 1, t[2], t[3] || 0, t[4] || 0, t[5] || 0));
    } else {
      d = new Date(t[0], t[1] - 1, t[2], t[3] || 0, t[4] || 0, t[5] || 0);
    }

    return d;
  }

  /**
   * Returns the date in a format suitable for the HTML input type "datetime-local" value
   *
   * @param {Date} date Current date
   * @returns {string} String representation of the date in ISO format, for instance 2021-03-09T23:50:55
   */
  static renderDateTimeLocal = (date: Date): string => {
    const dateTimeLocalValue = new Date(date.getTime() - date.getTimezoneOffset() * 60000)
      .toISOString()
      .slice(0, -5);

    return dateTimeLocalValue;
  };

  static renderAMPM = (hour: number): any => {
    const suffix = hour > 11 ? 'pm' : 'am';
    if (hour === 0) {
      return 12;
    } else if (hour === 12) {
      return 'Noon';
    } else if (hour > 12) {
      return hour - 12;
    } else {
      return hour;
    }
  };

  /**
   * Returns the date in "mm/dd/yyyy @ h:i" format
   *
   * @param {Date} date - The date to format
   * @returns {string} - Formatted date string like "06/03/2025 @ 8:45"
   */
  static renderReadableDateTime(date: Date): string {
    const pad = (n: number): string => String(n).padStart(2, '0');

    const month = pad(date.getMonth() + 1); // months are 0-based
    const day = pad(date.getDate());
    const year = date.getFullYear();

    let hour = date.getHours();
    const minutes = pad(date.getMinutes());

    const ampm = hour >= 12 ? 'PM' : 'AM';
    hour = hour % 12;
    hour = hour ? hour : 12; // the hour '0' should be '12'

    return `${month}/${day}/${year} @ ${hour}:${minutes} ${ampm}`;
  }

  /**
   * Returns the date in "h:i" format
   *
   * @param {Date} date - The date to format
   * @returns {string} - Formatted date string like "8:45"
   */
  static renderReadableTime(date: Date): string {
    const pad = (n: number): string => String(n).padStart(2, '0');

    let hour = date.getHours();
    const minutes = pad(date.getMinutes());

    const ampm = hour >= 12 ? 'PM' : 'AM';
    hour = hour % 12;
    hour = hour ? hour : 12; // the hour '0' should be '12'

    return `${hour}:${minutes} ${ampm}`;
  }

  /**
   * Returns the date in "mm/dd/yyyy" format
   *
   * @param {Date} date - The date to format
   * @returns {string} - Formatted date string like "06/03/2025"
   */
  static renderReadableDate(date: Date): string {
    const pad = (n: number): string => String(n).padStart(2, '0');

    const month = pad(date.getMonth() + 1); // months are 0-based
    const day = pad(date.getDate());
    const year = date.getFullYear();

    return `${month}/${day}/${year}`;
  }

  static renderAMPMString = (hour: number): string => {
    const suffix = hour > 11 ? 'pm' : 'am';
    if (hour === 12) {
      return '';
    } else {
      return suffix;
    }
  };

  /**
   * Returns the date in a format suitable for the HTML input type "date" value
   *
   * @param {Date} date Current date
   * @returns {string} String representation of the date in ISO format, for instance 2021-03-09
   */
  static renderDateLocal = (date: Date): string => {
    const dateTimeLocalValue = new Date(date.getTime() - date.getTimezoneOffset() * 60000)
      .toISOString()
      .slice(0, 10);

    return dateTimeLocalValue;
  };

  static chunk(arr: Array<any>, size: number): Array<any[]> {
    return Array.from({ length: Math.ceil(arr.length / size) }, (v, i) =>
      arr.slice(i * size, i * size + size)
    );
  }

  static dateTimeToTimeString(dateTime: Date): string {
    let hours = dateTime.getHours().toString();
    let minutes = dateTime.getMinutes().toString();

    if (hours.length == 1) {
      hours = '0' + hours;
    }

    if (minutes.length == 1) {
      minutes = '0' + minutes;
    }

    return hours + ':' + minutes;
  }

  static getLastDayOfMonth = (currentDate: Date): Date => {
    const end = new Date(currentDate);
    end.setMonth(end.getMonth() + 1);
    end.setDate(0);
    end.setHours(23);
    end.setMinutes(59);

    return end;
  };

  /**
   * Based on the given day, returnes the first day of the month
   *
   * @param {Date} currentDate Current date
   * @returns {Date} First day of the month
   */
  static getFirstDayOfMonth = (currentDate: Date): Date => {
    const start = new Date(currentDate);
    start.setDate(1);
    start.setHours(0);
    start.setMinutes(0);

    return start;
  };

  static dateTimeTo12HourTime(date: Date): string {
    let hours = date.getHours();
    const minutes = date.getMinutes();
    const ampm = hours >= 12 ? 'pm' : 'am';
    hours = hours % 12;
    hours = hours ? hours : 12; // the hour '0' should be '12'
    const strMinutes = minutes < 10 ? '0' + minutes : minutes;
    const strTime = hours + ':' + strMinutes + ' ' + ampm;
    return strTime;
  }

  /**
   * Get first day of current dates week, assuming Sunday is the first day of the week
   *
   * @param {Date} date Day you'd like to see
   * @returns {Date} First day of the week
   */
  static getFirstDayOfWeek = (date: Date): Date => {
    const targetDay = new Date(date);

    if (targetDay.getDay() !== 0) {
      targetDay.setDate(targetDay.getDate() - targetDay.getDay());
    }

    targetDay.setSeconds(0);
    targetDay.setMinutes(0);
    targetDay.setHours(0);

    return targetDay;
  };

  /**
   * Get last day of current dates week
   *
   * @param {Date} date Day you'd like to see
   * @returns {Date} Last day of the week
   */
  static getLastDayOfWeek = (date: Date): Date => {
    const lastDayOfWeek = this.getFirstDayOfWeek(date);
    lastDayOfWeek.setDate(lastDayOfWeek.getDate() + 6);

    lastDayOfWeek.setSeconds(59);
    lastDayOfWeek.setMinutes(59);
    lastDayOfWeek.setHours(23);

    return lastDayOfWeek;
  };

  /**
   * Convert a number of minutes into hours and minutes
   *
   * @param initialMinutes How many minutes you want to convert to hours and minutes
   * @returns {MinutesAndHoursResponse} Object containing the numebrs of hours and minutes in the given number of minutes
   */
  static minutesToHoursAndMinutes(initialMinutes: number): MinutesAndHoursResponse {
    // Store the input number of minutes in a variable num
    const num = initialMinutes;
    // Calculate the total hours by dividing the number of minutes by 60
    const hours = num / 60;
    // Round down the total hours to get the number of full hours
    const rhours = Math.floor(hours);
    // Calculate the remaining minutes after subtracting the full hours from the total hours
    const minutes = (hours - rhours) * 60;
    // Round the remaining minutes to the nearest whole number
    const rminutes = Math.round(minutes);

    const response: MinutesAndHoursResponse = {
      hours: rhours,
      minutes: rminutes,
    };

    return response;
  }

  /**
   * Converts a JS Date object into a date time string suitable for passing to our API
   *
   * @param {Date} dateTime Date time object to be converted
   * @returns {string} Date string in UTC timezone, in the following format 2023-07-01 14:30:00
   * @author Matt Lowden <matt.lowden@medicareprocrm.com>
   */
  static dateTimeToDateTimeString(dateTime: Date): string {
    return dateTime.toJSON().slice(0, 19).replace('T', ' ');
  }

  /**
   * Converts a JS Date object into a date string suitable for passing to our API
   *
   * @param {Date} date Date time object to be converted
   * @returns {string} Date string in UTC timezone, in the following format 2023-07-01
   * @author Matt Lowden <matt.lowden@medicareprocrm.com>
   */
  static dateToDateString(date: Date): string {
    if (!date) {
      return null;
    }

    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    const dateFormatted = `${yyyy}-${mm}-${dd}`;

    return dateFormatted;
  }

  static timeStringToDateTime(dateTime: string): Date {
    const dateTimeParsed = dateTime.split(':');
    const newDateTime = new Date();
    newDateTime.setHours(parseInt(dateTimeParsed[0]));
    newDateTime.setMinutes(parseInt(dateTimeParsed[1]));
    return newDateTime;
  }

  static convertSeconds(duration) {
    const hrs = Math.floor(duration / 3600);
    const mins = Math.floor((duration % 3600) / 60);
    const secs = Math.floor(duration % 60);

    let ret = '';

    if (hrs > 0) {
      ret += '' + hrs + ':' + (mins < 10 ? '0' : '');
    }

    ret += '' + mins + ':' + (secs < 10 ? '0' : '');
    ret += '' + secs;

    return ret;
  }

  static getMonthsBetweenDates(dateNow: Date, date: Date) {
    let months;
    months = (dateNow.getFullYear() - date.getFullYear()) * 12;
    months -= date.getMonth();
    months += dateNow.getMonth();
    return months <= 0 ? 0 : months;
  }

  static getTimeSince(date: Date, shorthand = false): string {
    const dateNow = new Date();
    const delta = Math.abs((dateNow.getTime() - date.getTime()) / 1000);
    const monthsBetween = this.getMonthsBetweenDates(dateNow, date);
    const yearsBetween = Math.floor(monthsBetween / 12);

    if (yearsBetween > 0) {
      return yearsBetween + (shorthand ? 'Y' : ' Year' + (yearsBetween > 1 ? 's' : ''));
    }

    if (monthsBetween > 1) {
      return monthsBetween + (shorthand ? 'M' : ' Month' + (monthsBetween > 1 ? 's' : ''));
    }

    const days = Math.floor(delta / 86400);
    if (days > 0) {
      return days + (shorthand ? 'D' : ' Day' + (days > 1 ? 's' : ''));
    }

    const hours = Math.floor(delta / 3600);
    if (hours > 0) {
      return hours + (shorthand ? 'H' : ' Hour' + (hours > 1 ? 's' : ''));
    }

    const minutes = Math.floor(delta / 60);
    if (minutes > 0) {
      return minutes + (shorthand ? 'M' : ' Minute' + (minutes > 1 ? 's' : ''));
    }

    return 'NOW';
  }

  static snakeCaseTo(string, newCase) {
    let newString;

    // CamelCase, camelCase
    if (newCase === 'camelCase') {
      newString = string.replace(/([-_][a-z])/g, (group) =>
        group.toUpperCase().replace('-', '').replace('_', '')
      );
    } else if (newCase === 'upperCamelCase') {
      newString = string.replace(/([-_][a-z])/g, (group) =>
        group.toUpperCase().replace('-', '').replace('_', '')
      );

      newString = this.capitalize(newString);
    }

    return newString;
  }

  static validateEmail(value) {
    if (value === undefined || value === null) {
      return false;
    }

    const validRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/;

    if (value.match(validRegex)) {
      return true;
    } else {
      return false;
    }
  }

  static getYears(numberOfYears: number, startYear: number = null): number[] {
    const currentYear = startYear ? startYear : new Date().getFullYear();
    const years: number[] = [];

    for (let year = currentYear; year >= currentYear - numberOfYears; year--) {
      years.push(year);
    }

    return years;
  }

  static getMonths() {
    return [
      'January',
      'February',
      'March',
      'April',
      'May',
      'June',
      'July',
      'August',
      'September',
      'October',
      'November',
      'December',
    ];
  }

  static getMonthByIndex(monthIndex, short = false) {
    let monthName = this.getMonths()[monthIndex];

    if (short) {
      monthName = monthName.substring(0, 3);
    }

    return monthName;
  }

  static capitalizeAll(str, lower = false) {
    return (lower ? str.toLowerCase() : str).replace(/(?:^|\s|["'([{])+\S/g, (match) =>
      match.toUpperCase()
    );
  }

  static stringToReference(string) {
    string = string.toLowerCase().replaceAll('_', ' ');

    string = Utilities.capitalizeAll(string);

    string = string.split(' ').join('');

    return string.charAt(0).toLowerCase() + string.substring(1);
  }

  static capitalize(string) {
    return string.charAt(0).toUpperCase() + string.slice(1);
  }

  static uniqueId() {
    return Math.random()
      .toString(36)
      .replace(/[^a-z]+/g, '')
      .substr(2, 10);
  }

  static merge(target, source) {
    const isObject = (obj) => obj && typeof obj === 'object';

    if (!isObject(target) || !isObject(source)) {
      return source;
    }

    Object.keys(source).forEach((key) => {
      const targetValue = target[key];
      const sourceValue = source[key];

      if (Array.isArray(targetValue) && Array.isArray(sourceValue)) {
        // This method merges arrays based on index
        target[key] = Object.values(Utilities.merge(Object.assign({}, targetValue), sourceValue));
      } else if (isObject(targetValue) && isObject(sourceValue)) {
        target[key] = Utilities.merge(Object.assign({}, targetValue), sourceValue);
      } else {
        target[key] = sourceValue;
      }
    });

    return target;
  }

  static escapeHtml(unsafe) {
    return unsafe
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  static hexToRgb(hex) {
    const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
    hex = hex.replace(shorthandRegex, function (m, r, g, b) {
      return r + r + g + g + b + b;
    });

    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result
      ? {
          r: parseInt(result[1], 16),
          g: parseInt(result[2], 16),
          b: parseInt(result[3], 16),
        }
      : null;
  }

  static findTurningDate(months) {
    const date = new Date();
    // date.setDate(date.getDate() + days);
    date.setMonth(date.getMonth() - months);
    // date.setFullYear(date.getFullYear() + years);

    const day = date.getDate();
    const month = date.getMonth() + 1;
    const year = date.getFullYear();

    return month.toString().padStart(2, '0') + '/' + day.toString().padStart(2, '0') + '/' + year;
  }

  static getRange(size, startAt = 0) {
    return [...Array.from(Array(size).keys())].map((i) => i + startAt);
  }

  static getStates() {
    return [
      {
        name: 'Alabama',
        abbreviation: 'AL',
      },
      {
        name: 'Alaska',
        abbreviation: 'AK',
      },
      {
        name: 'Arizona',
        abbreviation: 'AZ',
      },
      {
        name: 'Arkansas',
        abbreviation: 'AR',
      },
      {
        name: 'California',
        abbreviation: 'CA',
      },
      {
        name: 'Colorado',
        abbreviation: 'CO',
      },
      {
        name: 'Connecticut',
        abbreviation: 'CT',
      },
      {
        name: 'Delaware',
        abbreviation: 'DE',
      },
      {
        name: 'District Of Columbia',
        abbreviation: 'DC',
      },
      {
        name: 'Florida',
        abbreviation: 'FL',
      },
      {
        name: 'Georgia',
        abbreviation: 'GA',
      },
      {
        name: 'Hawaii',
        abbreviation: 'HI',
      },
      {
        name: 'Idaho',
        abbreviation: 'ID',
      },
      {
        name: 'Illinois',
        abbreviation: 'IL',
      },
      {
        name: 'Indiana',
        abbreviation: 'IN',
      },
      {
        name: 'Iowa',
        abbreviation: 'IA',
      },
      {
        name: 'Kansas',
        abbreviation: 'KS',
      },
      {
        name: 'Kentucky',
        abbreviation: 'KY',
      },
      {
        name: 'Louisiana',
        abbreviation: 'LA',
      },
      {
        name: 'Maine',
        abbreviation: 'ME',
      },
      {
        name: 'Maryland',
        abbreviation: 'MD',
      },
      {
        name: 'Massachusetts',
        abbreviation: 'MA',
      },
      {
        name: 'Michigan',
        abbreviation: 'MI',
      },
      {
        name: 'Minnesota',
        abbreviation: 'MN',
      },
      {
        name: 'Mississippi',
        abbreviation: 'MS',
      },
      {
        name: 'Missouri',
        abbreviation: 'MO',
      },
      {
        name: 'Montana',
        abbreviation: 'MT',
      },
      {
        name: 'Nebraska',
        abbreviation: 'NE',
      },
      {
        name: 'Nevada',
        abbreviation: 'NV',
      },
      {
        name: 'New Hampshire',
        abbreviation: 'NH',
      },
      {
        name: 'New Jersey',
        abbreviation: 'NJ',
      },
      {
        name: 'New Mexico',
        abbreviation: 'NM',
      },
      {
        name: 'New York',
        abbreviation: 'NY',
      },
      {
        name: 'North Carolina',
        abbreviation: 'NC',
      },
      {
        name: 'North Dakota',
        abbreviation: 'ND',
      },
      {
        name: 'Ohio',
        abbreviation: 'OH',
      },
      {
        name: 'Oklahoma',
        abbreviation: 'OK',
      },
      {
        name: 'Oregon',
        abbreviation: 'OR',
      },
      {
        name: 'Pennsylvania',
        abbreviation: 'PA',
      },
      {
        name: 'Puerto Rico',
        abbreviation: 'PR',
      },
      {
        name: 'Rhode Island',
        abbreviation: 'RI',
      },
      {
        name: 'South Carolina',
        abbreviation: 'SC',
      },
      {
        name: 'South Dakota',
        abbreviation: 'SD',
      },
      {
        name: 'Tennessee',
        abbreviation: 'TN',
      },
      {
        name: 'Texas',
        abbreviation: 'TX',
      },
      {
        name: 'Utah',
        abbreviation: 'UT',
      },
      {
        name: 'Vermont',
        abbreviation: 'VT',
      },
      {
        name: 'Virginia',
        abbreviation: 'VA',
      },
      {
        name: 'Washington',
        abbreviation: 'WA',
      },
      {
        name: 'West Virginia',
        abbreviation: 'WV',
      },
      {
        name: 'Wisconsin',
        abbreviation: 'WI',
      },
      {
        name: 'Wyoming',
        abbreviation: 'WY',
      },
    ];

    /*  {
                "name": "Guam",
                "abbreviation": "GU"
            }, 
            {
                "name": "Palau",
                "abbreviation": "PW"
            },
            */
  }

  static getAlphabet() {
    return [
      'A',
      'B',
      'C',
      'D',
      'E',
      'F',
      'G',
      'H',
      'I',
      'J',
      'K',
      'L',
      'M',
      'N',
      'O',
      'P',
      'Q',
      'R',
      'S',
      'T',
      'U',
      'V',
      'W',
      'X',
      'Y',
      'Z',
    ];
  }

  static phoneNumberCountryCode(phoneNumberString) {
    const cleaned = ('' + phoneNumberString).replace(/\D/g, '');
    const match = cleaned.match(/^(1|)?(\d{3})(\d{3})(\d{4})$/);
    if (match) {
      return ['+1', match[2], match[3], match[4]].join('');
    }
    return null;
  }

  static phoneNumberDisplay(phoneNumberString) {
    const cleaned = ('' + phoneNumberString).replace(/\D/g, '');
    const match = cleaned.match(/^(1|)?(\d{3})(\d{3})(\d{4})$/);
    if (match) {
      const intlCode = match[1] ? '' : '';
      return [intlCode, '(', match[2], ') ', match[3], '-', match[4]].join('');
    }
    return null;
  }

  /**
   * Create an ICS object or Gmail calendar link
   *
   * @param {string} title Event title
   * @param {string} description Short description of the event
   * @param {string} location Location of the event
   * @param {Date} start Event start date & time
   * @param {Date} end Event end date & time
   * @param {MakeCalendarDeliveryMethod} delivery How you want the event to be returned
   */

  static makeEvent(
    title: string,
    description: string,
    location: string,
    start: Date,
    finish: Date,
    calendar: MakeCalendarType,
    delivery: MakeCalendarDeliveryMethod
  ) {
    if (calendar == MakeCalendarType.gmail) {
      this.gmailMaker(title, description, location, start, finish, delivery);
    } else {
      this.icsMaker(title, description, location, start, finish, delivery);
    }
  }

  /**
   * Create an ICS object
   *
   * @param {string} title Event title
   * @param {string} description Short description of the event
   * @param {string} location Location of the event
   * @param {Date} start Event start date & time
   * @param {Date} end Event end date & time
   * @param {MakeCalendarDeliveryMethod} delivery How you want the event to be returned
   * @returns {Promise<any>} Returns the ICS object
   */
  static icsMaker(
    subject: string,
    description: string,
    location: string,
    start: Date,
    end: Date,
    delivery: MakeCalendarDeliveryMethod
  ): Promise<any> {
    const response = new Promise<any>((resolve, reject) => {
      const filename = 'event_' + subject + '.ics';

      const dateTimeStart: DateTime = [
        start.getFullYear(),
        start.getMonth() + 1,
        start.getDate(),
        start.getHours(),
        start.getMinutes(),
      ];

      const dateTimeEnd: DateTime = [
        end.getFullYear(),
        end.getMonth() + 1,
        end.getDate(),
        end.getHours(),
        end.getMinutes(),
      ];

      const event: EventAttributes = {
        title: subject,
        start: dateTimeStart,
        end: dateTimeEnd,
        ...(description ? { description } : {}),
        ...(location ? { location } : {}),
      };

      createEvent(event, (error, value) => {
        if (delivery === MakeCalendarDeliveryMethod.redirect) {
          const file = new File([value], filename, { type: 'text/calendar' });

          const url = URL.createObjectURL(file);
          const anchor = document.createElement('a');
          anchor.href = url;
          anchor.download = filename;

          document.body.appendChild(anchor);
          anchor.click();
          document.body.removeChild(anchor);

          URL.revokeObjectURL(url);
        }

        if (error) {
          reject(error);
        } else {
          resolve(value);
        }
      });
    });

    return response;
  }

  /**
   * Create a URL that sends the user to Gmail with the event fields already filled in
   *
   * @param {string} title Event title
   * @param {string} description Short description of the event
   * @param {string} location Location of the event
   * @param {Date} start Event start date & time
   * @param {Date} end Event end date & time
   * @param {MakeCalendarDeliveryMethod} delivery How you want the event to be returned
   * @returns {Promise<string>}
   */
  static gmailMaker(
    title: string,
    description: string,
    location: string,
    start: Date,
    end: Date,
    delivery: MakeCalendarDeliveryMethod
  ): Promise<string> {
    const response = new Promise<string>((resolve, reject) => {
      location = location ? location.replace(/,/g, '') : '';

      const dateTimeStart: string = ''.concat(
        start.getFullYear().toString(),
        this.dateMonthToTwoDigitString(start.getMonth()),
        this.dateComponentToTwoDigitString(start.getDate()),
        'T',
        this.dateComponentToTwoDigitString(start.getHours()),
        this.dateComponentToTwoDigitString(start.getMinutes()),
        '00'
      );

      const dateTimeEnd: string = ''.concat(
        end.getFullYear().toString(),
        this.dateMonthToTwoDigitString(end.getMonth()),
        this.dateComponentToTwoDigitString(end.getDate()),
        'T',
        this.dateComponentToTwoDigitString(end.getHours()),
        this.dateComponentToTwoDigitString(end.getMinutes()),
        '00'
      );

      const dateRange = ''.concat(dateTimeStart, '/', dateTimeEnd);

      const params = new URLSearchParams({
        text: title,
        dates: dateRange,
        details: description || '',
        location: location || '',
      });

      const gVars = 'https://calendar.google.com/calendar/r/eventedit?' + params.toString();

      if (delivery === MakeCalendarDeliveryMethod.redirect) {
        setTimeout(() => {
          window.open(gVars, '_blank');
        });
      }

      return resolve(gVars);
    });

    return response;
  }
}
